import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { LabActionInboxes, LabActionInboxSortOrderType, LabInboxes, LabSortOrderType, LabTabViews, SortDirection } from "@/Common/Models/UI/Enums";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { IJLabGraphsModel } from "../Models/JSONInterfaces/IJLabGraphsModel";
import { IJLabModel } from "../Models/JSONInterfaces/IJLabModel";
import { IJLabsActionInboxModel } from "../Models/JSONInterfaces/IJLabsActionInboxModel";
import { IJLabsInboxModel } from "../Models/JSONInterfaces/IJLabsInboxModel";
import { IJLabsTabModel } from "../Models/JSONInterfaces/IJLabsTabModel";
import { IJSideNoteModel } from "../Models/JSONInterfaces/IJSideNoteModel";
import { DefaultProvider, ILabsGlobalWrapper } from "../Models/UI/ILabsGlobalWrapper";

class LabsHelper {
    public static GetDefaultForLabsTabModel(): IJLabsTabModel {
        var jsn: IJLabsTabModel = {
            LabCount: 0,
            CurrentView: LabTabViews.Inbox,
            CurrentSpeciality: null,

            // Notes
            NoteWindowOpen: false,
            CurrentLabNotes: [],
            CurrentLabIDForNotes: "",
            CurrentLabNoteValue: "",
            NoteTopPosition: 0,
            NoteLeftPosition: 0,
            CurrentElementIDForNotes: "",
            Note_OBXName: "",
            Note_OBXUnit: "",
            Note_OBXValue: "",
            CurrentLabDetailsValue: "",

            // Actions
            Action_NoteWindowOpen: false,
            Action_CurrentLabIDForNotes: "",
            Action_CurrentLabNoteValue: "",
            Action_NoteTopPosition: 0,
            Action_NoteLeftPosition: 0,
            Action_CurrentElementIDForNotes: "",
            Action_CheckboxesNurses: [],
            Action_CheckboxesSecretaries: [],
            Action_OriginalOBXValue: "",
            Action_OBXName: "",
            Action_OBXUnit: "",

            // Action Details
            Action_DetailsWindowOpen: false,
            Action_DetailsTopPosition: 0,
            Action_DetailsLeftPosition: 0,
            Action_CurrentLabIDForActionDetails: "",
            Action_DetailsModel: null,

            // Action Counters
            ActionTotalCount: 0,
            ActionNursesCount: 0,
            ActionSecretariesCount: 0,
            PatientContactId: "",

            // Manual Lab Result Counters
            NewManualLabResultsCount: 0

        };

        return jsn;
    }

    public static GetDefaultForLabsInboxModel(): IJLabsInboxModel {
        var jsn: IJLabsInboxModel = {
            CurrentInbox: LabInboxes.New,
            TotalQueryPages: 1,
            TotalQueryRows: 0,
            CurrentPage: 1,
            SortColumn: LabSortOrderType.MessageDate,
            SortColumnDirection: SortDirection.DESC,
            SearchResultsColumnOrder: [],
            PageSizeOptions: [50, 100, 150],
            SelectedPageSizeOption: 50,
            SelectedPageNumber: 1,
            GlobalLabsearchText: "",
            GlobalLabsearchPlaceholder: "",
            LabsTableData: [],
            DateFrom: null,
            DateTo: null,
            LabInboxDisplaySettingsOption: false,
            SelectAllSitesChecked: true,
            Sites: [],
            CliniciansSearchText: "",
            SelectAllCliniciansChecked: false,
            ShowDeactivatedClinicians: false,
            CliniciansGlobalCache: [],
            CliniciansFiltered: [],
            IamPrescriberSelected: true,
            IamInCcSelected: true,
            SaveSettingsSelected: false,
            ShowAbnormalsOnly: false,
            ColumnOrderingPopupOpen: false,
            Initialized: false,
            PatientContactId: "",
            PortalStatuses: Immutable.List(PortalHelper.getPortalStatusCheckableList(CheckboxTarget.Myle_Labs_PortalStatus)),
            SentBySearchText: "",
            PortalLinkedItemModel: Immutable.fromJS(PortalHelper.getDefaultPortalLinkedItemModel()),
            IsSendToPatientPortalPopupOpen: false,
            SelectAllPortalSentBy: false,
            SelectAllPortalStatus: false,
            SentByGlobalCache: [],
            SentByFiltered: [],
            ShowEnrolledPatientsResults: true,
            IsFromPatient: false
        };
        return jsn;
    }

    public static GetDefaultForLabsActionInboxModel(): IJLabsActionInboxModel {
        var jsn: IJLabsActionInboxModel = {
            CurrentInbox: LabActionInboxes.All,
            TotalQueryPages: 1,
            TotalQueryRows: 0,
            TotalQueryRowsFiltered: 0,
            CurrentPage: 1,
            PageSizeOptions: [50, 100, 150],
            SelectedPageSizeOption: 50,
            SelectedPageNumber: 1,
            GlobalActionSearchText: "",
            GlobalActionsearchPlaceholder: "",
            ActionsTableData: [],
            ActionsTableDataFull: [],
            DateFrom: null,
            DateTo: null,
            ActionInboxDisplaySettingsOption: false,
            SelectAllSitesChecked: true,
            Sites: [],
            CliniciansPrescriptorSearchText: "",
            CliniciansActionBySearchText: "",
            SelectAllCliniciansPrescriptorChecked: false,
            SelectAllCliniciansActionByChecked: true,
            PrescriptorClinicians: [],
            ActionByClinicians: [],
            LabActionsInboxDisplaySettingsOption: false,
            ColumnOrderingPopupOpen: false,
            SortColumn: LabActionInboxSortOrderType.ActionAge,
            SortColumnDirection: SortDirection.DESC,
            Initialized: false,
            PatientContactId: ""
        };
        return jsn;
    }

    public static GetDefaultForLabModel(): IJLabModel {
        var jsn: IJLabModel = {
            ShowLinkPatientPopup: false,
            LabMessage: null,
            MylePatient: null,
            linkedIndividualIDs: [],
            ShowAdditionalDetails: false,
            ShowAbnormalAndCriticalObservations: false,
            ShowResults: true,
            IsNoteExpanded: true,
            SelectedPersonForLinkingLab: null,
            IsSaving: false,
            OnHoldCheckBox: false,
            PatientContactId: "",
            ReasonClickableTextVisible: false,
            LabClickableTextVisible: false,
            ImagingClickableTextVisible: false,
            RequestClickableTextVisible: false,
            ConsultClickableTextVisible: false,
            FollowUpPlanClickableTextVisible: false
        };

        return jsn;
    }

    public static GetDefaultForLabGraphModel(): IJLabGraphsModel {
        var jsn: IJLabGraphsModel = {
            Observations: {},
            ObservationTypesCheckBoxes: [],
            AreAllGraphsChecked: false,
            Id: ""
        };
        return jsn;
    }

    public static GetDefaultForSideNoteModel(): IJSideNoteModel {
        var jsn: IJSideNoteModel = {
            Raison: "",
            Laboratoire: "",
            Imagerie: "",
            Requete: "",
            UrgentRequete: false,
            Consultation: "",
            UrgentConsultation: false,
            ConsultationSpeciality: "",
            ConsultationDestiantaire: [],
            ConsultationDestinataireFreeText: "",
            FollowUp: "",
            Id: ""
        };
        return jsn;
    }

    public static BuildDefaultLabModelsForPatientProfile(patientId: string): ILabsGlobalWrapper {
        let globalWrapper: ILabsGlobalWrapper = Immutable.fromJS(DefaultProvider.Default());

        const labTabModel = LabsHelper.GetDefaultForLabsTabModel();
        labTabModel.PatientContactId = patientId;

        let speciality: FlatLookupView = null;
        const specialisations = sessionStore.getCurrentEmployee().getServiceSpecializations();
        if (specialisations && specialisations.length > 0) {
            speciality = specialisations[0];
        }

        labTabModel.CurrentSpeciality = speciality;
        labTabModel.Action_CheckboxesNurses = cacheStore
            .GetCheckboxesByLookupTypeCode("LabActions", null, false)
            .filter(y => y.Position >= 0 && y.Position <= 7)
            .sort(x => x.Position);
        labTabModel.Action_CheckboxesSecretaries = cacheStore
            .GetCheckboxesByLookupTypeCode("LabActions", null, false)
            .filter(y => y.Position >= 9)
            .sort(x => x.Position);

        globalWrapper = globalWrapper.set("Model", Immutable.fromJS(labTabModel));

        const labInboxModel = LabsHelper.GetDefaultForLabsInboxModel();
        labInboxModel.PatientContactId = patientId;
        labInboxModel.CurrentInbox = LabInboxes.All;
        labInboxModel.IsFromPatient = true;
        globalWrapper = globalWrapper.set("LabsInboxModel", Immutable.fromJS(labInboxModel));

        const labActionInboxModel = LabsHelper.GetDefaultForLabsActionInboxModel();
        labActionInboxModel.PatientContactId = patientId;
        globalWrapper = globalWrapper.set("LabsActionInboxModel", Immutable.fromJS(labActionInboxModel));

        const labModel = LabsHelper.GetDefaultForLabModel();
        labModel.PatientContactId = patientId;
        globalWrapper = globalWrapper.set("LabModel", Immutable.fromJS(labModel));

        const labGraphModel = LabsHelper.GetDefaultForLabGraphModel();
        labGraphModel.Id = patientId;
        globalWrapper = globalWrapper.set("LabGraphModel", Immutable.fromJS(labGraphModel));

        const sideNoteModel = LabsHelper.GetDefaultForSideNoteModel();
        sideNoteModel.Id = patientId;
        globalWrapper = globalWrapper.set("SideNoteModel", Immutable.fromJS(sideNoteModel));
        return globalWrapper;
    }

    public static GetDefaultPatientNameForLinkPatient(ddn: string, firstName: string, lastName: string): string {
        return `${ddn} ${firstName} ${lastName}`;
    }
}

export { LabsHelper };
